import ConfirmationDialog from 'core/components/ConfirmationDialog'
import { getDeleteConfirmText } from 'core/components/CRUDListContainer'
import React, { useState, useMemo } from 'react'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { deleteKaapiMachineDeployments } from 'k8s/components/kaapi/machine-deployment/actions'
import { deleteKaapiOpenstackMachineTemplates } from '../kaapi/openstack-machine-templates/actions'
import { deleteKaapiKudeadmConfigTemplates } from '../kaapi/kubeadm-config-templates/actions'
import { LAST_RUNNING_WORKER_NODE_DISACLAIMER } from './constant'
import { getDeleteText } from './DeleteWorkerNodesDialog'
import { updateNodePools } from '../kaapi/clusters/actions'

const DeleteNodeGroupsDialog = ({
  rows,
  onClose,
  isLastRunningNode,
  reloadMachineDeployments,
  cluster,
}) => {
  const [error, setError] = useState(null)
  const isClusterClass = useMemo(() => {
    return !!cluster?.spec?.topology
  }, [cluster])

  const {
    update: deleteMachineDeploymentFn,
    updating: deletingMachineDeployment,
  } = useUpdateAction(deleteKaapiMachineDeployments)
  const {
    update: deleteKaapiOpenstackMachineTemplatesFn,
    updating: deletingKaapiOpenstackMachineTemplates,
  } = useUpdateAction(deleteKaapiOpenstackMachineTemplates)
  const {
    update: deleteKaapiKudeadmConfigTemplatesFn,
    updating: deletingKaapiKudeadmConfigTemplates,
  } = useUpdateAction(deleteKaapiKudeadmConfigTemplates)
  const {
    update: updateNodePool,
    updating: updatingNodePools,
    error: updateNodePoolsError,
  } = useUpdateAction(updateNodePools)

  const deleteConfirmText = useMemo(() => {
    const deleteConfirmText = getDeleteConfirmText(rows.flatMap((nodeGroup) => nodeGroup.metadata))
    return getDeleteText(deleteConfirmText, isLastRunningNode)
  }, [rows, isLastRunningNode])

  const handleSubmit = async () => {
    const promises = []
    for (const nodeGroup of rows) {
      if (!nodeGroup?.kind) continue
      if (!isClusterClass) {
        // Delete the below functions after cluster migrations
        // Delete the machine deployment, openstack machine templates, and kubeadm config templates
        promises.push(
          deleteMachineDeploymentFn({
            name: nodeGroup.metadata.name,
            namespace: nodeGroup.metadata.namespace,
          }),
        )
        promises.push(
          deleteKaapiOpenstackMachineTemplatesFn({
            name: nodeGroup.metadata.name,
            namespace: nodeGroup.metadata.namespace,
          }),
        )
        promises.push(
          deleteKaapiKudeadmConfigTemplatesFn({
            name: nodeGroup.metadata.name,
            namespace: nodeGroup.metadata.namespace,
          }),
        )
      } else {
        const nodeIndex = cluster?.spec?.topology?.workers?.machineDeployments?.findIndex(
          (md) =>
            md?.name === nodeGroup?.metadata?.labels?.['topology.cluster.x-k8s.io/deployment-name'],
        )

        if (nodeIndex === -1) {
          console.error('Node deployment not found in cluster topology')
          continue
        }

        // Delete the node pool
        promises.push(
          updateNodePool({
            namespace: nodeGroup.metadata.namespace,
            body: [
              {
                op: 'remove',
                path: `/spec/topology/workers/machineDeployments/${nodeIndex}`,
              },
            ],
            clusterName: cluster?.metadata?.name,
          }),
        )
      }
    }

    // Wait for all promises to resolve
    try {
      await Promise.all(promises)
      reloadMachineDeployments(true)
      onClose(true)
    } catch (err) {
      console.error(err)
      setError({ title: 'Error Deleting Node Group', message: err?.message })
    }
  }

  return (
    <ConfirmationDialog
      text={deleteConfirmText}
      onConfirm={handleSubmit}
      onCancel={onClose}
      error={error || updateNodePoolsError}
      loading={
        deletingMachineDeployment ||
        deletingKaapiOpenstackMachineTemplates ||
        deletingKaapiKudeadmConfigTemplates ||
        updatingNodePools
      }
      open
      data-testid="delete-node-groups-dialog"
    />
  )
}

export default DeleteNodeGroupsDialog
